import React, { useState } from 'react';
import { Container, Row, Col, Accordion, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, HelpCircle, Shield, Database, Users, CreditCard } from 'lucide-react';
import Navbar from '../components/common/Navbar';

const FAQout = () => {
  const [activeKey, setActiveKey] = useState(null);

  const faqData = [
    {
      id: '0',
      icon: <HelpCircle className="me-2" size={20} />,
      question: "What does PhēnixLoc do for me?",
      answer: (
        <div>
          <p>PhēnixLoc provides secure storage for:</p>
          <ul>
            <li>Data and documents</li>
            <li>Passwords and login credentials</li>
            <li>Files and images</li>
            <li>Bank card information</li>
            <li>Digital wallet mnemonic words and phrases</li>
          </ul>
          <p>Additionally, PhēnixLoc provides an <strong>Endowment Center</strong> where upon death or disability of the account holder that prevents login, the application provides recovery of selected stored "Endowment" items by allowing transfer to a pre-defined recipient.</p>
        </div>
      )
    },
    {
      id: '1',
      icon: <Users className="me-2" size={20} />,
      question: "How do I create a PhēnixLoc account?",
      answer: (
        <div>
          <ol>
            <li>Enter your appropriate contact information</li>
            <li>Create a secure password</li>
            <li>Select your chosen plan</li>
          </ol>
          <div className="alert alert-warning mt-3">
            <strong>Important:</strong> When you register, make sure you write down and store your 12 mnemonic words that can be used to recapture your account if you forget your password.
          </div>
        </div>
      )
    },
    {
      id: '2',
      icon: <CreditCard className="me-2" size={20} />,
      question: "What are the available plans for PhēnixLoc?",
      answer: (
        <div>
          <div className="row">
            <div className="col-md-5 mb-3">
              <div className="card h-100">
                <div className="card-header bg-light">
                  <h5 className="card-title mb-0">Free Trial</h5>
                </div>
                <div className="card-body">
                  <ul className="list-unstyled">
                    <li><strong>Duration:</strong> 14 days</li>
                    <li><strong>Storage:</strong> 50MB</li>
                    <li><strong>Price:</strong> Free</li>
                  </ul>
                  <small className="text-muted">Can be upgraded to Basic or Plus plan</small>
                </div>
              </div>
            </div>
            <div className="col-md-5 mb-3">
              <div className="card h-100">
                <div className="card-header bg-primary text-white">
                  <h5 className="card-title mb-0">Basic Plan</h5>
                </div>
                <div className="card-body">
                  <ul className="list-unstyled">
                    <li><strong>Duration:</strong> 12 months</li>
                    <li><strong>Storage:</strong> 1GB</li>
                    <li><strong>Price:</strong> $5 USD</li>
                  </ul>
                  <small className="text-muted">Can be upgraded to Plus plan</small>
                </div>
              </div>
            </div>
            <div className="col-md-5 mb-3">
              <div className="card h-100">
                <div className="card-header bg-success text-white">
                  <h5 className="card-title text-black mb-0">Plus Plan</h5>
                </div>
                <div className="card-body">
                  <ul className="list-unstyled">
                    <li><strong>Duration:</strong> 12 months</li>
                    <li><strong>Storage:</strong> 5GB</li>
                    <li><strong>Price:</strong> $20 USD</li>
                  </ul>
                  <div className="badge bg-success">Includes Endowment Center</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: '3',
      icon: <Shield className="me-2" size={20} />,
      question: "How does an Endowment work?",
      answer: (
        <div>
          <h6>Setting up an Endowment:</h6>
          <ul>
            <li>The Endowment creator (Account Holder) sets up an Endowment in the Endowment Center</li>
            <li>Name the recipient by email address</li>
            <li>Choose files, bankcards, passwords, and blockchain data to include</li>
            <li>Endowment files and data can be set up and edited at any time</li>
          </ul>
          
          <h6>How the Endowment is triggered:</h6>
          <ul>
            <li>The Endowment is only transferred if the creator does not renew or cancel at the end of the year subscription</li>
            <li>If a creator's subscription expires, the Endowment becomes available to the recipient</li>
            <li>The creator will receive multiple email reminders when their account is ready for renewal</li>
          </ul>
          
          <h6>For Recipients:</h6>
          <ul>
            <li>Recipients need to acknowledge they have been appointed</li>
            <li>If the Endowment fires, recipients can log in and download the Endowment files and data</li>
            <li><strong>No PhēnixLoc account required</strong> for recipients</li>
          </ul>
        </div>
      )
    },
    {
      id: '4',
      icon: <Database className="me-2" size={20} />,
      question: "How do I know how much storage space I am using?",
      answer: (
        <div>
          <p>To check your storage usage:</p>
          <ol>
            <li>Log in to your account</li>
            <li>Select the <strong>Dashboard</strong> page</li>
          </ol>
          <p>This will show you:</p>
          <ul>
            <li>Memory used for each file category</li>
            <li>Total storage remaining for your selected plan</li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <>
    <Navbar />
    <Container className="py-5">
      <Row>
        <Col lg={10} className="mx-auto">
          <div className="text-center mb-5">
            <h1 className="display-4 fw-bold text-main mb-3">
              <HelpCircle className="me-3" size={48} />
              Frequently Asked Questions
            </h1>
            <p className="lead text-muted">
              Everything you need to know about PhēnixLoc and how it works.
            </p>
          </div>

          <Accordion activeKey={activeKey} onSelect={setActiveKey}>
            {faqData.map((faq) => (
              <Accordion.Item key={faq.id} eventKey={faq.id} className="mb-3 border rounded">
                <Accordion.Header className="fw-semibold">
                  <div className="d-flex align-items-center">
                    {faq.icon}
                    {faq.question}
                  </div>
                </Accordion.Header>
                <Accordion.Body className="pt-4">
                  {faq.answer}
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>

          <div className="text-center mt-5">
            <Card className="bg-light">
              <Card.Body>
                <h5>Still have questions?</h5>
                <p className="text-black mb-3">
                  Can't find the answer you're looking for? Please chat with our friendly team.
                </p>
                <Link to="/contact-us" className="navbar-link text-black">Contact Us</Link>
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>
    </Container>
    </>
  );
};

export default FAQout;